import { useState, useEffect } from 'react';
import { apiFetch, formatDate } from '../dppApi';

// The brand dashboard "Bequeathals" tab — a read-only transfer ledger for the
// brand's own pieces. A KPI summary sits at the top; below it, four collapsible
// groups mirror the Inquiries section's chevron pattern. This view reads only
// executed ownership events (GET /api/brand/bequeathals); pending inheritance
// designations are private to owners and never surface here.
const GROUPS = [
    { key: 'inheritance', title: 'Bequeathals', sum: 'Executed inheritances' },
    { key: 'gift', title: 'Gifts', sum: 'Gifts between owners' },
    { key: 'initial_sale', title: 'Initial sale', sum: 'Original point of sale' },
    { key: 'resale', title: 'Resale', sum: 'Secondary market sales' },
];

const BrandBequeathals = ({ token }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    // Bequeathals (inheritance) open by default; the rest start collapsed.
    const [collapsed, setCollapsed] = useState({ gift: true, initial_sale: true, resale: true });
    const toggle = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));

    // First-pass actions: brands are authorised to see the new owner's identity,
    // so "Reach out" / "Send a gift" open an email to them. Swap for a richer
    // in-app flow later. No-op when the transfer has no owner email on file.
    const reachOut = (r) => { if (r.to_email) window.location.href = `mailto:${r.to_email}`; };
    const sendGift = (r) => {
        if (r.to_email) window.location.href = `mailto:${r.to_email}?subject=${encodeURIComponent('A gift for you')}`;
    };

    useEffect(() => {
        apiFetch('/api/brand/bequeathals', { token })
            .then(setData)
            .catch(err => setError(err.message));
    }, [token]);

    if (error) return <p className="dpp-error">{error}</p>;
    if (!data) return <p className="collection-status collection-status--flush">Loading…</p>;

    const { summary, groups } = data;

    return (
        <div className="bequeathal-view">
            <div className="bequeathal-kpi-row">
                <div className="bequeathal-kpi">
                    <div className="bequeathal-kpi-n">{summary.in_circulation}</div>
                    <div className="bequeathal-kpi-l">Pieces in circulation</div>
                </div>
                <div className="bequeathal-kpi">
                    <div className="bequeathal-kpi-n">{summary.total_transfers}</div>
                    <div className="bequeathal-kpi-l">Total transfers</div>
                </div>
                <div className="bequeathal-kpi">
                    <div className="bequeathal-kpi-n">{summary.bequeathals}</div>
                    <div className="bequeathal-kpi-l">Bequeathals</div>
                </div>
            </div>

            {GROUPS.map(g => {
                const rows = groups[g.key] || [];
                const isCollapsed = !!collapsed[g.key];
                return (
                    <section className="inquiry-group" key={g.key}>
                        <button type="button" className="inquiry-group-title inquiry-group-toggle"
                            onClick={() => toggle(g.key)} aria-expanded={!isCollapsed}>
                            <span className={`inquiry-group-chevron${isCollapsed ? '' : ' is-open'}`} aria-hidden="true">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
                            </span>
                            <span>{g.title}</span>
                            <span className="inquiry-group-count">{rows.length}</span>
                            <span className="bequeathal-group-sum">{g.sum}</span>
                        </button>
                        {!isCollapsed && (
                            rows.length === 0
                                ? <p className="collection-status collection-status--flush section-empty">Nothing here yet.</p>
                                : (
                                    <div className="bequeathal-ledger">
                                        {rows.map((r, i) => (
                                            <div className="bequeathal-row" key={`${r.product_id}-${i}`}>
                                                <div className="bequeathal-row-top">
                                                    <span className="bequeathal-piece-name">{r.product_name || '—'}</span>
                                                    <span className="bequeathal-date">{formatDate(r.date)}</span>
                                                </div>
                                                <div className="bequeathal-row-parties">
                                                    <span className="bequeathal-flow">
                                                        <span className="bequeathal-from">{r.from_name || '—'}</span>
                                                        <span className="bequeathal-arrow">→</span>
                                                        <span className="bequeathal-to-name">{r.to_name || '—'}</span>
                                                    </span>
                                                    <span className="bequeathal-acts">
                                                        <button type="button" className="bequeathal-act" onClick={() => reachOut(r)}>Reach out</button>
                                                        <span className="bequeathal-act-sep">·</span>
                                                        <button type="button" className="bequeathal-act" onClick={() => sendGift(r)}>Send a gift</button>
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )
                        )}
                    </section>
                );
            })}
        </div>
    );
};

export default BrandBequeathals;
